import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  chakra,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Check, Mic, Pause, Play, RotateCcw, Square, Trash2, Video } from 'lucide-react';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { testIds } from './testIds';
import {
  useMediaRecorder,
  type RecorderErrorReason,
  type RecorderState,
  type RecordingKind,
} from './useMediaRecorder';

export { isMediaRecordingSupported, type RecordingKind } from './useMediaRecorder';

type SaveStatus = 'idle' | 'saving' | 'failed';

const REMAINING_TIME_WARNING_MS = 60_000;
const VIDEO_MAX_HEIGHT = pxToRem(360);

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

function deviceLabel(kind: RecordingKind): string {
  return kind === 'audio' ? 'microphone' : 'camera and microphone';
}

function statusMessage(state: RecorderState, kind: RecordingKind, isInNativeApp: boolean): string {
  switch (state.status) {
    case 'idle':
      return '';
    case 'requestingPermission':
      return `Waiting for permission to use your ${deviceLabel(kind)}. Select Allow if your ${isInNativeApp ? 'phone' : 'browser'} asks.`;
    case 'ready':
      return kind === 'audio'
        ? 'Your microphone is ready. Speak to check the level, then start recording.'
        : 'Your camera is ready. Check your framing, then start recording.';
    case 'recording':
      return 'Recording';
    case 'paused':
      return 'Recording paused';
    case 'processing':
      return 'Preparing your recording';
    case 'recorded':
      return 'Play your recording back. Save it, or record again if you want another try.';
    case 'error':
      return errorMessage(state.reason, kind, isInNativeApp);
  }
}

function errorMessage(
  reason: RecorderErrorReason,
  kind: RecordingKind,
  isInNativeApp: boolean,
): string {
  const device = deviceLabel(kind);
  switch (reason) {
    case 'unsupported':
      return 'Your browser cannot record here. Upload a file instead.';
    case 'permissionDenied':
      return isInNativeApp
        ? `We could not use your ${device}. Allow access for the Duke app in your phone's Settings, then try again. You can also upload a file instead.`
        : `We could not use your ${device}. Allow access in your browser settings, then try again. You can also upload a file instead.`;
    case 'deviceNotFound':
      return `No ${device} was found. Connect one, then try again.`;
    case 'deviceInUse':
      return `Your ${device} is being used by another app. Close that app, then try again.`;
    case 'recordingFailed':
      return 'Something went wrong while recording. Try again, or upload a file instead.';
  }
}

function LivePreview({ stream }: { stream: MediaStream }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video === null) {
      return;
    }
    video.srcObject = stream;
    return () => {
      video.srcObject = null;
    };
  }, [stream]);

  return (
    <chakra.video
      ref={videoRef}
      data-testid={testIds.livePreview}
      aria-label="Camera preview"
      autoPlay
      muted
      playsInline
      w="full"
      maxH={VIDEO_MAX_HEIGHT}
      bg="black"
      borderRadius="md"
      objectFit="contain"
      transform="scaleX(-1)"
    />
  );
}

function LevelMeter({ stream, isActive }: { stream: MediaStream; isActive: boolean }) {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window.AudioContext === 'undefined' || stream.getAudioTracks().length === 0) {
      return;
    }
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);
    void audioContext.resume();

    const samples = new Uint8Array(analyser.fftSize);
    let frame = 0;

    // Writes straight to the element so the meter does not re-render React
    // sixty times a second.
    const draw = () => {
      analyser.getByteTimeDomainData(samples);
      let peak = 0;
      for (const sample of samples) {
        peak = Math.max(peak, Math.abs(sample - 128) / 128);
      }
      if (barRef.current !== null) {
        barRef.current.style.transform = `scaleX(${Math.min(1, peak * 1.5)})`;
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(frame);
      source.disconnect();
      void audioContext.close();
    };
  }, [stream]);

  return (
    <Box
      data-testid={testIds.levelMeter}
      role="img"
      aria-label="Microphone level"
      w="full"
      h={2}
      bg="bg.muted"
      borderRadius="full"
      overflow="hidden"
    >
      <Box
        ref={barRef}
        h="full"
        bg={isActive ? 'red.500' : 'brand.500'}
        borderRadius="full"
        transform="scaleX(0)"
        transformOrigin="left"
        transition="transform 0.08s linear"
      />
    </Box>
  );
}

function RecordingDot({ isActive }: { isActive: boolean }) {
  return (
    <Box
      as="span"
      display="inline-block"
      boxSize={iconSize.sm}
      borderRadius="full"
      bg={isActive ? 'red.500' : 'gray.400'}
      aria-hidden
    />
  );
}

function RecordingTimer({
  elapsedMs,
  maxDurationMs,
  isRecording,
}: {
  elapsedMs: number;
  maxDurationMs: number;
  isRecording: boolean;
}) {
  const remainingMs = maxDurationMs - elapsedMs;

  return (
    <VStack spacing={1} alignItems="flex-start">
      <HStack spacing={2}>
        <RecordingDot isActive={isRecording} />
        <Text
          data-testid={testIds.timer}
          role="timer"
          fontSize="lg"
          fontWeight="semibold"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
          color="fg.default"
        >
          {formatDuration(elapsedMs)}
          <Text as="span" fontWeight="normal" color="fg.muted">
            {' / '}
            {formatDuration(maxDurationMs)}
          </Text>
        </Text>
      </HStack>
      {remainingMs <= REMAINING_TIME_WARNING_MS ? (
        <Text fontSize="sm" color="fg.muted">
          Recording stops automatically in {formatDuration(Math.max(0, remainingMs))}.
        </Text>
      ) : null}
    </VStack>
  );
}

function Playback({ kind, url }: { kind: RecordingKind; url: string }) {
  if (kind === 'video') {
    return (
      <chakra.video
        data-testid={testIds.playback}
        src={url}
        controls
        playsInline
        preload="metadata"
        w="full"
        maxH={VIDEO_MAX_HEIGHT}
        bg="black"
        borderRadius="md"
      />
    );
  }
  return (
    <chakra.audio data-testid={testIds.playback} src={url} controls preload="metadata" w="full" />
  );
}

export function MediaRecorderPanel({
  kind,
  maxDurationMs,
  onSave,
  onClose,
  onUnsavedRecordingChange,
  saveBlockedMessage,
  isInNativeApp = false,
}: {
  kind: RecordingKind;
  maxDurationMs: number;
  onSave: (file: File) => Promise<void> | void;
  onClose: () => void;
  onUnsavedRecordingChange?: (hasUnsavedRecording: boolean) => void;
  saveBlockedMessage?: string;
  isInNativeApp?: boolean;
}) {
  const recorder = useMediaRecorder({ kind, maxDurationMs });
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const { state, elapsedMs, hasUnsavedRecording, prepare, discard } = recorder;
  const primaryActionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    void prepare();
  }, [prepare]);

  useEffect(() => {
    onUnsavedRecordingChange?.(hasUnsavedRecording);
  }, [hasUnsavedRecording, onUnsavedRecordingChange]);

  useEffect(() => () => onUnsavedRecordingChange?.(false), [onUnsavedRecordingChange]);

  // The buttons change with every step, so keep keyboard focus on the action
  // the student is most likely to want next.
  useEffect(() => {
    primaryActionRef.current?.focus();
  }, [state.status]);

  const close = () => {
    discard();
    onClose();
  };

  // The take only exists in this browser, so it is kept until the upload has
  // started and the student can save again if it fails.
  const save = async (file: File) => {
    setSaveStatus('saving');
    try {
      await onSave(file);
      close();
    } catch {
      setSaveStatus('failed');
    }
  };

  const recordAgain = () => {
    setSaveStatus('idle');
    void prepare();
  };

  const KindIcon = kind === 'audio' ? Mic : Video;
  const message = statusMessage(state, kind, isInNativeApp);

  return (
    <VStack
      data-testid={testIds.panel}
      w="full"
      spacing={4}
      alignItems="stretch"
      p={4}
      borderWidth="1px"
      borderRadius="lg"
      borderColor="border.emphasis"
      bg="bg.subtle"
    >
      <HStack spacing={2}>
        <KindIcon size={iconSize.lg} aria-hidden />
        <Text fontWeight="semibold" color="fg.default">
          {kind === 'audio' ? 'Record audio' : 'Record video'}
        </Text>
      </HStack>

      {state.status === 'error' ? (
        <Alert status="error" borderRadius="md" role="status">
          <AlertIcon />
          <AlertDescription data-testid={testIds.status}>{message}</AlertDescription>
        </Alert>
      ) : (
        <Text data-testid={testIds.status} role="status" fontSize="sm" color="fg.muted">
          {message}
        </Text>
      )}

      <RecorderBody kind={kind} state={state} elapsedMs={elapsedMs} maxDurationMs={maxDurationMs} />

      {state.status === 'recorded' && saveStatus === 'failed' ? (
        <Alert status="error" borderRadius="md" role="status">
          <AlertIcon />
          <AlertDescription>
            Your recording could not be saved. Check your connection, then select Save recording
            again.
          </AlertDescription>
        </Alert>
      ) : null}

      {state.status === 'recorded' && saveBlockedMessage !== undefined ? (
        <Alert status="warning" borderRadius="md" role="status">
          <AlertIcon />
          <AlertDescription>{saveBlockedMessage}</AlertDescription>
        </Alert>
      ) : null}

      <RecorderActions
        state={state}
        recorder={recorder}
        primaryActionRef={primaryActionRef}
        isSaving={saveStatus === 'saving'}
        isSaveBlocked={saveBlockedMessage !== undefined}
        onSave={(file) => void save(file)}
        onRecordAgain={recordAgain}
        onClose={close}
      />
    </VStack>
  );
}

function RecorderBody({
  kind,
  state,
  elapsedMs,
  maxDurationMs,
}: {
  kind: RecordingKind;
  state: RecorderState;
  elapsedMs: number;
  maxDurationMs: number;
}) {
  switch (state.status) {
    case 'requestingPermission':
    case 'processing':
      return (
        <HStack justifyContent="center" py={4}>
          <Spinner size="md" color="fg.muted" />
        </HStack>
      );
    case 'ready':
    case 'recording':
    case 'paused':
      return (
        <VStack spacing={3} alignItems="stretch">
          {kind === 'video' ? <LivePreview stream={state.stream} /> : null}
          {state.status !== 'ready' ? (
            <RecordingTimer
              elapsedMs={elapsedMs}
              maxDurationMs={maxDurationMs}
              isRecording={state.status === 'recording'}
            />
          ) : null}
          <LevelMeter stream={state.stream} isActive={state.status === 'recording'} />
        </VStack>
      );
    case 'recorded':
      return (
        <VStack spacing={2} alignItems="stretch">
          <Playback kind={kind} url={state.recording.url} />
          <Text fontSize="sm" color="fg.muted">
            Length {formatDuration(state.recording.durationMs)}
          </Text>
        </VStack>
      );
    case 'idle':
    case 'error':
      return null;
  }
}

function RecorderActions({
  state,
  recorder,
  primaryActionRef,
  isSaving,
  isSaveBlocked,
  onSave,
  onRecordAgain,
  onClose,
}: {
  state: RecorderState;
  recorder: ReturnType<typeof useMediaRecorder>;
  primaryActionRef: RefObject<HTMLButtonElement | null>;
  isSaving: boolean;
  isSaveBlocked: boolean;
  onSave: (file: File) => void;
  onRecordAgain: () => void;
  onClose: () => void;
}) {
  const cancelButton = (
    <Button data-testid={testIds.cancelButton} variant="ghost" onClick={onClose}>
      Cancel
    </Button>
  );

  switch (state.status) {
    case 'idle':
    case 'requestingPermission':
    case 'processing':
      return <HStack spacing={2}>{cancelButton}</HStack>;
    case 'ready':
      return (
        <HStack spacing={2} flexWrap="wrap">
          <Button
            ref={primaryActionRef}
            data-testid={testIds.startButton}
            leftIcon={<RecordingDot isActive />}
            onClick={recorder.start}
          >
            Start recording
          </Button>
          {cancelButton}
        </HStack>
      );
    case 'recording':
    case 'paused':
      return (
        <HStack spacing={2} flexWrap="wrap">
          <Button
            ref={primaryActionRef}
            data-testid={testIds.stopButton}
            leftIcon={<Square size={iconSize.md} fill="currentColor" aria-hidden />}
            onClick={recorder.stop}
          >
            Stop
          </Button>
          {state.status === 'recording' ? (
            <Button
              data-testid={testIds.pauseButton}
              variant="secondary"
              leftIcon={<Pause size={iconSize.md} aria-hidden />}
              onClick={recorder.pause}
            >
              Pause
            </Button>
          ) : (
            <Button
              data-testid={testIds.resumeButton}
              variant="secondary"
              leftIcon={<Play size={iconSize.md} aria-hidden />}
              onClick={recorder.resume}
            >
              Resume
            </Button>
          )}
        </HStack>
      );
    case 'recorded':
      return (
        <HStack spacing={2} flexWrap="wrap">
          <Button
            ref={primaryActionRef}
            data-testid={testIds.saveButton}
            leftIcon={<Check size={iconSize.md} aria-hidden />}
            isLoading={isSaving}
            loadingText="Saving"
            isDisabled={isSaveBlocked}
            onClick={() => onSave(state.recording.file)}
          >
            Save recording
          </Button>
          <Button
            data-testid={testIds.recordAgainButton}
            variant="secondary"
            leftIcon={<RotateCcw size={iconSize.md} aria-hidden />}
            isDisabled={isSaving}
            onClick={onRecordAgain}
          >
            Record again
          </Button>
          <Button
            data-testid={testIds.discardButton}
            variant="ghost"
            leftIcon={<Trash2 size={iconSize.md} aria-hidden />}
            isDisabled={isSaving}
            onClick={onClose}
          >
            Discard
          </Button>
        </HStack>
      );
    case 'error':
      return (
        <HStack spacing={2} flexWrap="wrap">
          {state.reason !== 'unsupported' ? (
            <Button
              ref={primaryActionRef}
              data-testid={testIds.retryButton}
              leftIcon={<RotateCcw size={iconSize.md} aria-hidden />}
              onClick={() => void recorder.prepare()}
            >
              Try again
            </Button>
          ) : null}
          {cancelButton}
        </HStack>
      );
  }
}
